import { useMemo } from 'react';

import type {
  CompositionV3Filters,
  CompositionV4BreakdownReq,
  CompositionV4Req,
} from '@revelio/data-access';

import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../filters/hooks/useAvailableDateRangeWithTrialLag';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getV3MonthRefs } from '../../filters/utils/get-v3-month-refs';
import {
  buildParentLookupFromTree,
  type NestedTreemapParent,
} from '../Chart/utils/buildNestedTreemapData';
import {
  DataProviderQueryStatus,
  type LevelDimension,
  type TableData,
} from '../DataProvider/types';
import {
  buildCompositionV4Request,
  isCompositionV4RequestComplete,
  isSnapshotQuery,
} from './build-composition-v4-request';
import {
  getCompositionV4TableBreakdowns,
  uniqueBreakdowns,
} from './composition-v4-breakdowns';
import {
  type CompositionV4TableSource,
  getCompositionV4Metrics,
  getCompositionV4TableSources,
} from './composition-v4-tables';
import {
  convertCompositionV4Breakdown,
  convertCompositionV4BreakdownSeries,
  convertCompositionV4BreakdownTree,
  convertCompositionV4Data,
  convertCompositionV4Timeseries,
} from './convert-composition-v4-data';
import type { PlannedQuery } from './plan-data-queries';
import type { QueryResult, QueryTables } from './resolve-items';
import {
  findLadderLevel,
  LEVELS_OF_TAXONOMY,
  type TaxonomyDimension,
  taxonomyOfLevel,
} from './taxonomy-levels';
import { useCompositionV4Filters } from './use-composition-v4-filters';
import {
  type CompositionV4Response,
  useCompositionV4Query,
} from './use-composition-v4-query';
import { type TaxonomyTrees, useTaxonomyTrees } from './use-taxonomy-trees';

type TableBreakdowns = Readonly<
  Record<string, readonly CompositionV4BreakdownReq[]>
>;

export type CompositionV4Drilldown = { level: LevelDimension; id: number };

type LevelFilterKey = keyof CompositionV3Filters;

const levelFilterKey = (level: LevelDimension): LevelFilterKey =>
  level
    .replace(/^role_lvl_/, 'role_level_')
    .replace(/^skill_lvl_/, 'skills_level_')
    .replace(/^activity_lvl_/, 'activities_level_') as LevelFilterKey;

export const narrowCompositionV4Request = <
  Req extends { filters: CompositionV3Filters },
>(
  req: Req,
  { level, id }: CompositionV4Drilldown
): Req => {
  const filters = { ...req.filters };
  for (const taxonomyLevel of levelsOfTaxonomy(taxonomyOfLevel(level))) {
    delete filters[levelFilterKey(taxonomyLevel)];
  }
  return { ...req, filters: { ...filters, [levelFilterKey(level)]: [id] } };
};

const levelsOfTaxonomy = (
  taxonomy: TaxonomyDimension
): readonly LevelDimension[] => LEVELS_OF_TAXONOMY[taxonomy];

const getTableBreakdowns = (
  sources: Readonly<Record<string, CompositionV4TableSource>>
): TableBreakdowns =>
  Object.keys(sources).reduce<
    Record<string, readonly CompositionV4BreakdownReq[]>
  >((breakdowns, key) => {
    const source = sources[key];
    if (source?.kind === 'breakdown') {
      breakdowns[key] = getCompositionV4TableBreakdowns(source);
    }
    return breakdowns;
  }, {});

const sublevelOf = (
  source: CompositionV4TableSource | undefined
): LevelDimension | undefined => {
  if (source?.kind !== 'breakdown') return undefined;
  const [, sublevel] = source.dimensions;
  return sublevel;
};

type ParentLookups = Readonly<
  Record<string, ReadonlyMap<string, NestedTreemapParent>>
>;

const getParentLookups = (
  sources: Readonly<Record<string, CompositionV4TableSource>>,
  trees: TaxonomyTrees,
  geographySelectionListIds: readonly string[]
): ParentLookups =>
  Object.keys(sources).reduce<
    Record<string, ReadonlyMap<string, NestedTreemapParent>>
  >((lookups, key) => {
    const sublevel = sublevelOf(sources[key]);
    const child =
      sublevel && findLadderLevel(sublevel, geographySelectionListIds);
    if (sublevel && child) {
      const tree = trees[taxonomyOfLevel(sublevel)] ?? [];
      lookups[key] = buildParentLookupFromTree(tree, child.depth);
    }
    return lookups;
  }, {});

const convertTables = ({
  data,
  primaryDimension,
  sources,
  breakdowns,
  parentLookups,
  monthRefs,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  sources: Readonly<Record<string, CompositionV4TableSource>>;
  breakdowns: TableBreakdowns;
  parentLookups: ParentLookups;
  monthRefs: readonly string[];
}): QueryTables =>
  Object.keys(sources).reduce<Record<string, TableData>>((converted, key) => {
    const source = sources[key];
    if (source?.kind === 'breakdown') {
      const [column, sublevel] = source.dimensions;
      const [breakdown, sublevelBreakdown] = breakdowns[key] ?? [];
      if (source.timeseries && breakdown) {
        converted[key] = convertCompositionV4BreakdownSeries({
          data,
          primaryDimension,
          column,
          breakdown,
          monthRefs,
        });
      } else if (sublevel && breakdown && sublevelBreakdown) {
        converted[key] = convertCompositionV4BreakdownTree({
          data,
          primaryDimension,
          columns: [column, sublevel],
          breakdowns: [breakdown, sublevelBreakdown],
          parentOf: parentLookups[key] ?? new Map(),
        });
      } else if (breakdown) {
        converted[key] = convertCompositionV4Breakdown({
          data,
          primaryDimension,
          column,
          breakdown,
        });
      }
    } else if (source?.timeseries) {
      converted[key] = convertCompositionV4Timeseries({
        data,
        primaryDimension,
        metrics: source.metrics,
        monthRefs,
      });
    } else if (source) {
      converted[key] = convertCompositionV4Data({
        data,
        primaryDimension,
        metrics: source.metrics,
      });
    }
    return converted;
  }, {});

const FILTERS_LOADING: QueryResult = {
  data: undefined,
  status: DataProviderQueryStatus.Fetching,
};

export const useCompositionV4Tables = ({
  primaryDimension,
  tables,
  drilldown,
  pause = false,
}: {
  primaryDimension: PrimaryFilterId;
  tables: PlannedQuery['tables'];
  drilldown?: CompositionV4Drilldown;
  pause?: boolean;
}): QueryResult | null => {
  const { filters, isLoading } = useCompositionV4Filters({ primaryDimension });
  const pageReq = buildCompositionV4Request({
    dimension: primaryDimension,
    filters,
    isSnapshot: isSnapshotQuery(tables),
  });
  const result = useCompositionV4RequestTables({
    primaryDimension,
    tables,
    req: drilldown ? narrowCompositionV4Request(pageReq, drilldown) : pageReq,
    pause: pause || isLoading,
  });
  return isLoading && !pause ? FILTERS_LOADING : result;
};

export const useCompositionV4RequestTables = ({
  primaryDimension,
  tables,
  req: entitiesReq,
  pause = false,
}: {
  primaryDimension: PrimaryFilterId;
  tables: PlannedQuery['tables'];
  req: CompositionV4Req;
  pause?: boolean;
}): QueryResult | null => {
  const sources = useMemo(
    () => getCompositionV4TableSources(primaryDimension, tables),
    [primaryDimension, tables]
  );
  const metrics = getCompositionV4Metrics(sources);
  const { geographySelectionListIds } = useFilterContext();
  const breakdowns = useMemo(() => getTableBreakdowns(sources), [sources]);
  const requestedBreakdowns = uniqueBreakdowns(
    Object.values(breakdowns).flat()
  );
  const req =
    requestedBreakdowns.length > 0
      ? { ...entitiesReq, breakdowns: requestedBreakdowns }
      : entitiesReq;

  const treeDimensions = new Set(
    Object.values(sources).reduce<string[]>((dimensions, source) => {
      const sublevel = sublevelOf(source);
      if (sublevel) dimensions.push(taxonomyOfLevel(sublevel));
      return dimensions;
    }, [])
  );
  const { trees, isLoading: isTreesLoading } = useTaxonomyTrees(treeDimensions);
  const parentLookups = useMemo(
    () => getParentLookups(sources, trees, geographySelectionListIds),
    [sources, trees, geographySelectionListIds]
  );

  const { availableDateRange } = useAvailableDateRangeWithTrialLag();

  const isLoading = !availableDateRange;
  const isComplete =
    Object.keys(sources).length > 0 && isCompositionV4RequestComplete(req);
  const { data, status, error } = useCompositionV4Query({
    req,
    metrics,
    pause: pause || !isComplete,
  });

  const queryTables = useMemo(() => {
    const monthRefs = availableDateRange
      ? getV3MonthRefs({ availableDateRange, filters: {} })
      : [];

    return data
      ? convertTables({
          data,
          primaryDimension,
          sources,
          breakdowns,
          parentLookups,
          monthRefs,
        })
      : undefined;
  }, [
    data,
    primaryDimension,
    sources,
    breakdowns,
    parentLookups,
    availableDateRange,
  ]);

  const canQuery = !pause && (isLoading || isComplete);
  const resultStatus =
    isTreesLoading && status !== DataProviderQueryStatus.Error
      ? DataProviderQueryStatus.Fetching
      : status;

  return useMemo(
    () =>
      canQuery ? { data: queryTables, status: resultStatus, error } : null,
    [canQuery, queryTables, resultStatus, error]
  );
};
